import { EmailProviderType, PersonalEmailStatus, OutlookMessageSummary, formatLocalDisplayDate } from "./personalEmailApi";

// Pure helpers extracted out of emailview.tsx so the provider -> displayed-email mapping and the POP3
// client-side search filter are unit-testable without mounting the component.

/**
 * The SMTP/POP3 branch's "connected as" address comes straight off the provider-status response (unlike Gmail,
 * whose profile lookup is a separate, deliberately-lazy call -- see fetchGmailConnectedEmail in emailview.tsx).
 * Returns "" for a non-SMTP status so callers can call this unconditionally.
 */
export function resolveSmtpConnectedEmail(status: Pick<PersonalEmailStatus, "providerType" | "fromEmail" | "username">): string {
  if (status.providerType !== EmailProviderType.CustomPopSmtp) return "";
  return status.fromEmail ?? status.username ?? "";
}

/**
 * POP3 has no server-side search (see PersonalSmtpProvider.cs) -- filters the already-fetched inbox window
 * client-side by subject/from-name instead of hiding the shared search box.
 */
export function filterThreadsBySearch<T extends { subject?: string | null; fromName?: string | null }>(
  threads: T[],
  searchTerm: string
): T[] {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return threads;
  return threads.filter(
    (thread) =>
      (thread.subject ?? "").toLowerCase().includes(term) ||
      (thread.fromName ?? "").toLowerCase().includes(term)
  );
}

/**
 * Maps a page of Outlook (Graph) message summaries into the same flat shape threadlist/Inbox already renders
 * for SMTP, extended with the isStarred/isImportant flags ShowOutlookMessage's action buttons need. Extracted
 * as a pure function (mirroring the SMTP mapping precedent) so emailview.tsx's onGetOutlookMessages callback
 * stays a thin wiring layer and this reshaping logic is unit-testable without mounting the component.
 */
export function mapOutlookMessages(messages: OutlookMessageSummary[]) {
  return messages.map((m) => ({
    id: m.id,
    fromName: m.fromName ?? m.fromEmail ?? "",
    fromEmail: m.fromEmail,
    toName: m.toName ?? null,
    toEmail: m.toEmail ?? null,
    subject: m.subject,
    // Always local: the backend's DisplayDate is formatted on the raw UTC value, so it disagreed with the
    // opened message's (local) time by the viewer's UTC offset.
    displayDate: formatLocalDisplayDate(m.date),
    date: m.date,
    isStarred: m.isStarred,
    isImportant: m.isImportant,
  }));
}

/** Splits a comma-separated recipient string ("a@x.com, b@y.com") into trimmed, de-duplicated parts. */
export function splitRecipientList(value: string | null | undefined): string[] {
  const seen = new Set<string>();
  return (value ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter((part) => {
      const key = part.toLowerCase();
      if (!part || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

/**
 * Who a reply should go to: normally the original sender, but when the message was sent by the connected
 * mailbox itself (opened from Sent), replying to "yourself" is wrong -- target its original recipients.
 */
export function resolveReplyRecipients(
  message: { fromEmail?: string | null; toEmail?: string | null } | null | undefined,
  connectedEmail: string | null | undefined
): string[] {
  const fromEmail = (message?.fromEmail ?? "").trim();
  const ownAddress = (connectedEmail ?? "").trim().toLowerCase();
  const sentByMe = !!ownAddress && fromEmail.toLowerCase() === ownAddress;
  if (sentByMe) {
    const recipients = splitRecipientList(message?.toEmail);
    if (recipients.length > 0) return recipients;
  }
  return fromEmail ? [fromEmail] : [];
}

/** Sent-folder rows show who the message went to (like Outlook/Gmail); every other folder shows the sender. */
export function getListRowName(
  email: { fromName?: string | null; toName?: string | null; toEmail?: string | null },
  isSentFolder: boolean
): string {
  if (isSentFolder) {
    const recipient = email.toName || email.toEmail;
    if (recipient) return recipient;
  }
  return email.fromName ?? "";
}

/**
 * Recomputes each Gmail thread's list-row displayDate from its raw date in the viewer's local timezone, the
 * same way mapOutlookMessages/onGetInboxMessages already do for Outlook/SMTP -- the backend's own DisplayDate
 * is formatted against DateTime.UtcNow with no timezone conversion (bug/email-date-display). Keeps every other
 * field on the thread object untouched (contractor/subject/etc. come straight from the API response).
 */
export function recomputeLocalDisplayDates<T extends { date?: string | null }>(threads: T[]): T[] {
  return threads.map((thread) => ({ ...thread, displayDate: formatLocalDisplayDate(thread.date) }));
}

/**
 * Dispatches a tab-count refresh to whichever provider is actually connected, so callers (Compose/ShowEmail's
 * send/reply/forward/mark-important/delete success paths, plus emailview.tsx's poll) don't need to know which
 * provider that is. SMTP has no counts concept server-side -- neither branch fires for it (bug/ui-bugfix-batch9
 * item 9).
 */
export function refreshEmailCounts(
  isGmailConnected: boolean,
  isOutlookConnected: boolean,
  onGetEmailCount: () => void,
  onGetOutlookMessageCounts: () => void
): void {
  if (isGmailConnected) onGetEmailCount();
  else if (isOutlookConnected) onGetOutlookMessageCounts();
}

/**
 * Whether the counts poll's current tick should actually do work -- skipped while the tab is hidden so a
 * backgrounded tab doesn't keep firing network requests every 30s (bug/ui-bugfix-batch9 item 9).
 */
export function shouldRefreshCountsOnPollTick(isDocumentHidden: boolean): boolean {
  return !isDocumentHidden;
}

/** The GetImages registry key to render for a given attachment mime type. */
export type AttachmentIconKey = "imageIcon" | "pdfImage" | "DocIcon" | "BlueFile";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"];

/**
 * Maps an opened-message attachment's mime type to the GetImages key to render for it -- the same
 * four-way discrimination Compose.tsx's own attachment list already branches on (image / PDF / DOCX /
 * generic), reapplied here for remote EmailAttachment metadata rather than a locally-staged File object
 * (bug/email-attachments-outlook item 1). Returns a key name rather than the resolved asset value so this
 * file keeps its existing "pure data shaping, no UI import" boundary (see mapOutlookMessages above) --
 * callers resolve the actual asset via GetImages[key] at the render call site. No live image thumbnail is
 * produced for image/* here (unlike Compose's URL.createObjectURL(file) case) -- a static icon is used
 * instead, since fetching every image attachment's bytes just to preview it on every message open would be
 * an avoidable cost; see the plan's Design principle.
 */
export function getAttachmentIcon(
  mimeType: string | null | undefined,
  filename?: string | null
): AttachmentIconKey {
  if (mimeType?.startsWith("image/")) return "imageIcon";
  if (mimeType === "application/pdf") return "pdfImage";
  if (mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") return "DocIcon";
  // Mail servers often send attachments as a generic application/octet-stream, so fall back to the extension.
  const extension = filename?.split(".").pop()?.toLowerCase() ?? "";
  if (IMAGE_EXTENSIONS.includes(extension)) return "imageIcon";
  if (extension === "pdf") return "pdfImage";
  if (extension === "docx") return "DocIcon";
  // The thin gray outline icon is barely visible at 20px in light mode.
  return "BlueFile";
}
